import { Navigate, Route, Routes } from "react-router-dom";
import { lazy, Suspense } from "react";
import { LoadingSpinner } from "./components/LoadingSpinner";
import { UserRole } from "./types/user";
import { ProtectedRoute } from "./components/security/ProtectedRoute";
import { RoleBasedRoute } from "./components/security/RoleBasedRoute";

// Lazy load page components for code splitting
// Auth pages
const Home = lazy(() =>
  import("./pages/auth/Home").then((module) => ({ default: module.Home })),
);
const PublicLandingPage = lazy(() =>
  import("./pages/public/PublicLandingPage").then((module) => ({
    default: module.PublicLandingPage,
  })),
);
const Callback = lazy(() =>
  import("./pages/auth/Callback").then((module) => ({
    default: module.Callback,
  })),
);
const FaqPage = lazy(() =>
  import("./pages/public/FaqPage").then((module) => ({
    default: module.FaqPage,
  })),
);
const PrivacyPolicyPage = lazy(() =>
  import("./pages/public/PrivacyPolicyPage").then((module) => ({
    default: module.PrivacyPolicyPage,
  })),
);
const CookiePolicyPage = lazy(() =>
  import("./pages/public/CookiePolicyPage").then((module) => ({
    default: module.CookiePolicyPage,
  })),
);
const AccessibilityStatementPage = lazy(() =>
  import("./pages/public/AccessibilityStatementPage").then((module) => ({
    default: module.AccessibilityStatementPage,
  })),
);
const TermsOfServicePage = lazy(() =>
  import("./pages/public/TermsOfServicePage").then((module) => ({
    default: module.TermsOfServicePage,
  })),
);

// Dashboard pages
const Dashboard = lazy(() =>
  import("./pages/dashboard/Dashboard").then((module) => ({
    default: module.Dashboard,
  })),
);

// Organization pages
const Organizations = lazy(() =>
  import("./pages/organizations/Organizations").then((module) => ({
    default: module.Organizations,
  })),
);
const OrganizationRequests = lazy(() =>
  import("./pages/organizations/OrganizationRequests").then((module) => ({
    default: module.OrganizationRequests,
  })),
);
const OrganizationProfile = lazy(() =>
  import("./pages/organizations/OrganizationProfile").then((module) => ({
    default: module.OrganizationProfile,
  })),
);

// User pages
const UserManagement = lazy(() =>
  import("./pages/users/UserManagement").then((module) => ({
    default: module.UserManagement,
  })),
);
const UserProfile = lazy(() =>
  import("./pages/users/UserProfile").then((module) => ({
    default: module.UserProfile,
  })),
);

// Project pages
const ProjectManagement = lazy(() =>
  import("./pages/projects/ProjectManagement").then((module) => ({
    default: module.ProjectManagement,
  })),
);
const ProjectProfile = lazy(() =>
  import("./pages/projects/ProjectProfile").then((module) => ({
    default: module.ProjectProfile,
  })),
);

// Task pages
const TaskManagement = lazy(() =>
  import("./pages/tasks/TaskManagement").then((module) => ({
    default: module.TaskManagement,
  })),
);
const TaskProfile = lazy(() =>
  import("./pages/tasks/TaskProfile").then((module) => ({
    default: module.TaskProfile,
  })),
);

// Feedback pages
const FeedbackManagement = lazy(() =>
  import("./pages/feedback/FeedbackManagement").then((module) => ({
    default: module.FeedbackManagement,
  })),
);
const ProjectFeedbackDetailPage = lazy(() =>
  import("./pages/feedback/ProjectFeedbackDetailPage").then((module) => ({
    default: module.ProjectFeedbackDetailPage,
  })),
);
const TaskCommentDetailPage = lazy(() =>
  import("./pages/feedback/TaskCommentDetailPage").then((module) => ({
    default: module.TaskCommentDetailPage,
  })),
);

// Audit pages
const AuditLogs = lazy(() =>
  import("./pages/audit/AuditLogs").then((module) => ({
    default: module.AuditLogs,
  })),
);

export function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route
        path="/"
        element={
          <Suspense
            fallback={<LoadingSpinner fullScreen message="Loading..." />}
          >
            <PublicLandingPage />
          </Suspense>
        }
      />
      <Route
        path="/welcome"
        element={
          <Suspense
            fallback={<LoadingSpinner fullScreen message="Loading..." />}
          >
            <Home />
          </Suspense>
        }
      />
      <Route
        path="/auth/callback"
        element={
          <Suspense
            fallback={<LoadingSpinner fullScreen message="Loading..." />}
          >
            <Callback />
          </Suspense>
        }
      />
      <Route
        path="/faq"
        element={
          <Suspense
            fallback={<LoadingSpinner fullScreen message="Loading..." />}
          >
            <FaqPage />
          </Suspense>
        }
      />
      <Route
        path="/privacy-policy"
        element={
          <Suspense
            fallback={<LoadingSpinner fullScreen message="Loading..." />}
          >
            <PrivacyPolicyPage />
          </Suspense>
        }
      />
      <Route
        path="/terms-of-service"
        element={
          <Suspense
            fallback={<LoadingSpinner fullScreen message="Loading..." />}
          >
            <TermsOfServicePage />
          </Suspense>
        }
      />
      <Route
        path="/cookie-policy"
        element={
          <Suspense
            fallback={<LoadingSpinner fullScreen message="Loading..." />}
          >
            <CookiePolicyPage />
          </Suspense>
        }
      />
      <Route
        path="/accessibility-statement"
        element={
          <Suspense
            fallback={<LoadingSpinner fullScreen message="Loading..." />}
          >
            <AccessibilityStatementPage />
          </Suspense>
        }
      />

      {/* Protected Routes - Unified Routes */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <RoleBasedRoute
              allowedRoles={[UserRole.SUPER_ADMIN, UserRole.ORG_ADMIN]}
            >
              <Suspense
                fallback={
                  <LoadingSpinner fullScreen message="Loading dashboard..." />
                }
              >
                <Dashboard />
              </Suspense>
            </RoleBasedRoute>
          </ProtectedRoute>
        }
      />

      {/* Organizations - Super Admin Only */}
      <Route
        path="/organizations"
        element={
          <ProtectedRoute>
            <RoleBasedRoute allowedRoles={[UserRole.SUPER_ADMIN]}>
              <Suspense
                fallback={
                  <LoadingSpinner
                    fullScreen
                    message="Loading organizations..."
                  />
                }
              >
                <Organizations />
              </Suspense>
            </RoleBasedRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/organization-requests"
        element={
          <ProtectedRoute>
            <RoleBasedRoute allowedRoles={[UserRole.SUPER_ADMIN]}>
              <Suspense
                fallback={
                  <LoadingSpinner
                    fullScreen
                    message="Loading organization requests..."
                  />
                }
              >
                <OrganizationRequests />
              </Suspense>
            </RoleBasedRoute>
          </ProtectedRoute>
        }
      />

      {/* Organization Profile - Both Roles */}
      <Route
        path="/organization/:organizationId?"
        element={
          <ProtectedRoute>
            <RoleBasedRoute
              allowedRoles={[UserRole.SUPER_ADMIN, UserRole.ORG_ADMIN]}
            >
              <Suspense
                fallback={
                  <LoadingSpinner
                    fullScreen
                    message="Loading organization..."
                  />
                }
              >
                <OrganizationProfile />
              </Suspense>
            </RoleBasedRoute>
          </ProtectedRoute>
        }
      />

      {/* Users - Both Roles */}
      <Route
        path="/users"
        element={
          <ProtectedRoute>
            <RoleBasedRoute
              allowedRoles={[UserRole.SUPER_ADMIN, UserRole.ORG_ADMIN]}
            >
              <Suspense
                fallback={
                  <LoadingSpinner fullScreen message="Loading users..." />
                }
              >
                <UserManagement />
              </Suspense>
            </RoleBasedRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/user/:userId"
        element={
          <ProtectedRoute>
            <RoleBasedRoute
              allowedRoles={[UserRole.SUPER_ADMIN, UserRole.ORG_ADMIN]}
            >
              <Suspense
                fallback={
                  <LoadingSpinner fullScreen message="Loading user..." />
                }
              >
                <UserProfile />
              </Suspense>
            </RoleBasedRoute>
          </ProtectedRoute>
        }
      />

      {/* Projects - Both Roles */}
      <Route
        path="/projects"
        element={
          <ProtectedRoute>
            <RoleBasedRoute
              allowedRoles={[UserRole.SUPER_ADMIN, UserRole.ORG_ADMIN]}
            >
              <Suspense
                fallback={
                  <LoadingSpinner fullScreen message="Loading projects..." />
                }
              >
                <ProjectManagement />
              </Suspense>
            </RoleBasedRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/project/:projectId"
        element={
          <ProtectedRoute>
            <RoleBasedRoute
              allowedRoles={[UserRole.SUPER_ADMIN, UserRole.ORG_ADMIN]}
            >
              <Suspense
                fallback={
                  <LoadingSpinner fullScreen message="Loading project..." />
                }
              >
                <ProjectProfile />
              </Suspense>
            </RoleBasedRoute>
          </ProtectedRoute>
        }
      />

      {/* Tasks - Both Roles */}
      <Route
        path="/tasks"
        element={
          <ProtectedRoute>
            <RoleBasedRoute
              allowedRoles={[
                UserRole.SUPER_ADMIN,
                UserRole.ORG_ADMIN,
                UserRole.COORDINATOR,
                UserRole.VOLUNTEER,
              ]}
            >
              <Suspense
                fallback={
                  <LoadingSpinner fullScreen message="Loading tasks..." />
                }
              >
                <TaskManagement />
              </Suspense>
            </RoleBasedRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/task/:taskId"
        element={
          <ProtectedRoute>
            <RoleBasedRoute
              allowedRoles={[
                UserRole.SUPER_ADMIN,
                UserRole.ORG_ADMIN,
                UserRole.COORDINATOR,
                UserRole.VOLUNTEER,
              ]}
            >
              <Suspense
                fallback={
                  <LoadingSpinner fullScreen message="Loading task..." />
                }
              >
                <TaskProfile />
              </Suspense>
            </RoleBasedRoute>
          </ProtectedRoute>
        }
      />

      {/* Feedback - Coordinators and Admins */}
      <Route
        path="/feedback"
        element={
          <ProtectedRoute>
            <RoleBasedRoute
              allowedRoles={[UserRole.SUPER_ADMIN, UserRole.ORG_ADMIN]}
            >
              <Suspense
                fallback={
                  <LoadingSpinner fullScreen message="Loading feedback..." />
                }
              >
                <FeedbackManagement />
              </Suspense>
            </RoleBasedRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path="/feedback/project/:id"
        element={
          <ProtectedRoute>
            <RoleBasedRoute
              allowedRoles={[UserRole.SUPER_ADMIN, UserRole.ORG_ADMIN]}
            >
              <Suspense
                fallback={
                  <LoadingSpinner fullScreen message="Loading feedback..." />
                }
              >
                <ProjectFeedbackDetailPage />
              </Suspense>
            </RoleBasedRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path="/feedback/task-comment/:id"
        element={
          <ProtectedRoute>
            <RoleBasedRoute
              allowedRoles={[UserRole.SUPER_ADMIN, UserRole.ORG_ADMIN]}
            >
              <Suspense
                fallback={
                  <LoadingSpinner fullScreen message="Loading feedback..." />
                }
              >
                <TaskCommentDetailPage />
              </Suspense>
            </RoleBasedRoute>
          </ProtectedRoute>
        }
      />

      {/* Audit Logs - Super Admin Only */}
      <Route
        path="/audit-logs"
        element={
          <ProtectedRoute>
            <RoleBasedRoute
              allowedRoles={[UserRole.SUPER_ADMIN, UserRole.ORG_ADMIN]}
            >
              <Suspense
                fallback={
                  <LoadingSpinner fullScreen message="Loading audit logs..." />
                }
              >
                <AuditLogs />
              </Suspense>
            </RoleBasedRoute>
          </ProtectedRoute>
        }
      />

      {/* Catch all - redirect to home, but allow callback route */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
